import type { AccessDeviceManagementAttempt } from '@/api/core/access-device-management';
import { putAccessDeviceManagement } from '@/api/core/access-device-management';
import type { AccessDeviceRegistryItem } from '@/api/core/access-device-registry';
import { canWriteProperties } from '@/api/core/authorization';
import type { NativeProperty } from '@/api/core/native-property-read';
import { isApiError } from '@/api/http/errors';
import { useOperatorSession } from '@/auth/runtime';
import { UnsavedGuard } from '@/components/unsaved-guard';
import {
  Alert,
  AlertDescription,
  Button,
  ConfirmDialog,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mgrvtechgroup/admin-ds';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useInsertionEffect, useRef, useState } from 'react';
import {
  accessDeviceCommandContextQuery,
  nativePropertiesQuery,
  nativePropertyCatalogQuery,
  type PropertyRuntime,
} from '../property/property-queries';

interface ScopedAttempt extends AccessDeviceManagementAttempt {
  readonly apiNamespace: string;
  readonly generation: number;
}

function propertyLabel(properties: NativeProperty[], propertyId: string): string {
  const item = properties.find((property) => property.id === propertyId);
  return item ? `${item.name} · ${item.code}` : propertyId;
}

function failureMessage(failure: unknown): string {
  if (isApiError(failure)) {
    if (failure.status === 400) return '퇴역 요청을 확인하지 못했습니다. 초안은 보존했습니다.';
    if (failure.status === 403) return '현재 지점의 수정 권한을 확인할 수 없습니다.';
    if (failure.status === 404)
      return '퇴역 범위를 확인하지 못했습니다. 퇴역 여부를 별도로 확인해 주세요.';
    if (failure.status === 409)
      return '저장 정보가 바뀌었습니다. 최신 정보를 다시 확인한 뒤 검토해 주세요.';
    if (
      failure.kind === 'network' ||
      failure.kind === 'timeout' ||
      failure.kind === 'invalid-response' ||
      (failure.status !== undefined && failure.status >= 500)
    )
      return '저장 결과를 확인하지 못했습니다. 같은 퇴역 요청을 자동으로 다시 보내지 않았습니다.';
  }
  return '퇴역 결과를 확인하지 못했습니다. 입력한 초안을 보존했습니다.';
}

export function AccessDeviceRetirementForm({
  runtime,
  generation,
  device,
  onClose,
  refreshRegistry,
  refreshRegistryAfterSave,
}: {
  runtime: PropertyRuntime;
  generation: number;
  device: AccessDeviceRegistryItem;
  onClose: () => void;
  refreshRegistry: () => Promise<AccessDeviceRegistryItem | undefined>;
  refreshRegistryAfterSave: () => Promise<{ failed: boolean }>;
}) {
  const session = useOperatorSession(runtime.session);
  const [propertyId, setPropertyId] = useState('');
  const [attempt, setAttempt] = useState<ScopedAttempt | null>(null);
  const [pending, setPending] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState<string>();
  const [readError, setReadError] = useState(false);
  const [reconcileRequired, setReconcileRequired] = useState(false);
  const [receipt, setReceipt] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const flight = useRef<object | null>(null);
  const controller = useRef<AbortController | null>(null);
  const activeScope = useRef({
    apiNamespace: runtime.apiNamespace,
    generation,
    deviceId: device.core_device_id,
    propertyId,
  });
  useInsertionEffect(() => {
    activeScope.current = {
      apiNamespace: runtime.apiNamespace,
      generation,
      deviceId: device.core_device_id,
      propertyId,
    };
  }, [runtime.apiNamespace, generation, device.core_device_id, propertyId]);

  const canWrite =
    session.status === 'authorized' &&
    session.generation === generation &&
    !session.deniedDomains.includes('property') &&
    Boolean(session.authorization && canWriteProperties(session.authorization));
  const [propertyCursor, setPropertyCursor] = useState<string | null>(null);
  const properties = useQuery(nativePropertiesQuery(runtime, generation, propertyCursor, canWrite));
  const currentScope = () => {
    const current = runtime.session.getSnapshot();
    return (
      current.status === 'authorized' &&
      current.generation === generation &&
      activeScope.current.apiNamespace === runtime.apiNamespace &&
      activeScope.current.generation === generation &&
      activeScope.current.deviceId === device.core_device_id &&
      activeScope.current.propertyId === propertyId
    );
  };
  const candidateContext = useQuery(
    accessDeviceCommandContextQuery(
      runtime,
      generation,
      device.core_device_id,
      null,
      true,
      () => currentScope() || !propertyId,
    ),
  );
  const context = useQuery(
    accessDeviceCommandContextQuery(
      runtime,
      generation,
      device.core_device_id,
      propertyId || null,
      true,
      currentScope,
    ),
  );
  const catalog = useQuery(
    nativePropertyCatalogQuery(
      runtime,
      generation,
      propertyId,
      canWrite && Boolean(propertyId),
      currentScope,
    ),
  );
  const eligibleIds = candidateContext.data?.command_property_ids ?? [];
  const imported = candidateContext.data?.requires_property_selection === true;
  const selectedProperty = catalog.data?.data.property;
  const validProperty = Boolean(
    propertyId &&
      context.data &&
      !context.data.requires_property_selection &&
      context.data.command_property_ids.length === 1 &&
      context.data.command_property_ids[0] === propertyId &&
      selectedProperty?.id === propertyId,
  );
  const contextMatchesRow = context.data?.revision === device.revision;
  const canStart =
    canWrite &&
    validProperty &&
    contextMatchesRow &&
    !pending &&
    !attempt &&
    !uncertain &&
    !receipt &&
    !reconcileRequired;
  const dirty = !receipt && (Boolean(propertyId) || Boolean(attempt) || uncertain);

  useEffect(() => () => controller.current?.abort(), []);

  const close = () => {
    controller.current?.abort();
    onClose();
  };

  const submit = async (sameAttempt = false) => {
    if (flight.current !== null || !canWrite) return;
    const activeFlight = {};
    flight.current = activeFlight;
    setPending(true);
    let captured = attempt;
    const priorUncertain = uncertain;
    let abort: AbortController | null = null;
    const isCurrent = () => {
      const current = runtime.session.getSnapshot();
      return (
        current.status === 'authorized' &&
        current.generation === generation &&
        runtime.apiNamespace === (captured?.apiNamespace ?? runtime.apiNamespace) &&
        activeScope.current.apiNamespace === runtime.apiNamespace &&
        activeScope.current.generation === generation &&
        activeScope.current.deviceId === device.core_device_id &&
        activeScope.current.propertyId === propertyId &&
        !abort?.signal.aborted
      );
    };
    try {
      if (!sameAttempt) {
        if (!canStart) return;
        setError(undefined);
        let latest: AccessDeviceRegistryItem | undefined;
        try {
          latest = await refreshRegistry();
          const latestContext = await context.refetch();
          if (!latest) {
            setError(
              '최신 장비 정보와 퇴역 범위를 확인하지 못했습니다. 초안은 보존했습니다. 다시 조회해 주세요.',
            );
            return;
          }
          if (
            latest.core_device_id !== device.core_device_id ||
            latest.retired ||
            latestContext.isError ||
            latest.revision !== latestContext.data?.revision ||
            latest.revision !== device.revision ||
            !currentScope()
          ) {
            setError(
              '최신 장비 정보와 퇴역 범위를 확인하지 못했습니다. 초안은 보존했습니다. 다시 조회해 주세요.',
            );
            return;
          }
        } catch {
          setError(
            '최신 장비 정보와 퇴역 범위를 확인하지 못했습니다. 초안은 보존했습니다. 다시 조회해 주세요.',
          );
          return;
        }
        const body = Object.freeze({
          command_id: crypto.randomUUID(),
          expected_revision: latest.revision,
          device_id: latest.device_id,
          gateway_id: latest.gateway_id,
          is_active: latest.is_active,
          slot_ids: Object.freeze([] as string[]),
          device_type: latest.device_type,
          retired: true,
          device_name: latest.device_name,
          gateway_name: latest.gateway_name,
          battery: latest.battery,
        });
        captured = Object.freeze({
          property_id: propertyId,
          core_device_id: latest.core_device_id,
          body,
          apiNamespace: runtime.apiNamespace,
          generation,
        });
        setAttempt(captured);
      }
      if (!captured) return;
      setError(undefined);
      abort = new AbortController();
      controller.current = abort;
      if (!captured.core_device_id) throw new Error('missing Core device UUID');
      const saved = await putAccessDeviceManagement(
        runtime.client,
        captured.property_id,
        captured.core_device_id,
        captured.body,
        abort.signal,
      );
      if (!isCurrent()) return;
      if (!saved.device.retired || saved.device.slot_ids.length !== 0)
        throw new Error('retirement snapshot mismatch');
      setAttempt(null);
      setUncertain(false);
      setReceipt(true);
      try {
        const refreshed = await refreshRegistryAfterSave();
        if (refreshed.failed) setReadError(true);
      } catch {
        setReadError(true);
      }
    } catch (failure) {
      if (!isCurrent()) return;
      if (isApiError(failure) && failure.status === 403)
        runtime.session.reportDomainAccessDenied('property', generation);
      if (
        !priorUncertain &&
        isApiError(failure) &&
        (failure.status === 400 || failure.status === 409 || failure.status === 404)
      )
        setAttempt(null);
      if (isApiError(failure) && failure.status === 409) {
        setReconcileRequired(true);
        setError(failureMessage(failure));
      }
      if (
        isApiError(failure) &&
        (failure.kind === 'network' ||
          failure.kind === 'timeout' ||
          failure.kind === 'invalid-response' ||
          (failure.status !== undefined && failure.status >= 500))
      )
        setUncertain(true);
      if (isApiError(failure) && failure.status === 404 && priorUncertain) {
        setUncertain(true);
        setError(
          '퇴역 여부를 확인하지 못했습니다. 같은 범위의 재확인에서도 장비가 보이지 않아 저장 여부를 확정할 수 없습니다.',
        );
      } else setError(failureMessage(failure));
    } finally {
      if (flight.current === activeFlight) {
        flight.current = null;
        setPending(false);
        if (controller.current === abort) controller.current = null;
      }
    }
  };

  const allowedIds = imported ? (properties.data?.data ?? []).map((item) => item.id) : eligibleIds;

  return (
    <section aria-label="기존 장비 퇴역" className="space-y-4 rounded-md border p-4 sm:p-5">
      <UnsavedGuard
        when={canWrite && (dirty || pending)}
        suppressConfirmation={closeConfirmOpen}
        autoProceedWhenClean={false}
        warning={{
          target: uncertain ? '결과가 불확실한 퇴역 요청' : '퇴역 초안',
          action: '초안을 버리고 이동',
          consequence: uncertain
            ? '앞선 요청이 이미 저장되었을 수 있습니다. 화면을 떠나면 같은 경로로 재확인할 수 없습니다.'
            : '이동하면 선택한 퇴역 초안이 사라집니다.',
        }}
      />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="title-sm">기존 장비 퇴역</h2>
          <p className="body-sm text-muted-foreground mt-1">
            퇴역은 Core 등록 정보를 표시 대상에서 제외하고 저장된 연결을 해제합니다.
          </p>
        </div>
        <Button
          variant="outline"
          disabled={pending}
          onClick={() => {
            if (dirty) setCloseConfirmOpen(true);
            else close();
          }}
        >
          닫기
        </Button>
      </div>
      <dl className="grid gap-2 sm:grid-cols-2">
        <div></div>
        <div>
          <dt className="caption text-muted-foreground">장비 ID</dt>
          <dd className="body break-all">{device.device_id}</dd>
        </div>
        <div>
          <dt className="caption text-muted-foreground">현재 저장된 연결</dt>
          <dd className="body">{device.slot_ids.length}개</dd>
        </div>
      </dl>
      <p className="caption text-muted-foreground">
        장비를 물리적으로 끄거나 출입 권한을 회수하지 않습니다.
      </p>
      {!canWrite && (
        <p className="body-sm text-muted-foreground">
          현재 지점 수정 권한이 없어 퇴역할 수 없습니다.
        </p>
      )}
      {candidateContext.isError && (
        <Alert variant="warning">
          <AlertDescription>
            장비의 퇴역 범위를 확인할 수 없습니다. 지점을 추정하지 않았습니다.
          </AlertDescription>
        </Alert>
      )}
      {!candidateContext.isError &&
        !candidateContext.isPending &&
        allowedIds.length === 0 &&
        !imported && (
          <Alert variant="warning">
            <AlertDescription>수정 가능한 지점 범위를 확인할 수 없습니다.</AlertDescription>
          </Alert>
        )}
      {allowedIds.length > 0 && (
        <label className="flex flex-col gap-1">
          <span className="caption">퇴역 범위 지점</span>
          <Select
            value={propertyId}
            disabled={pending || Boolean(attempt) || uncertain || receipt}
            onValueChange={(value) => {
              if (value) {
                setPropertyId(value);
                setError(undefined);
              }
            }}
          >
            <SelectTrigger aria-label="퇴역 범위 지점" className="min-h-11 w-full">
              <SelectValue placeholder="지점을 선택하세요">
                {(value) =>
                  typeof value === 'string' ? propertyLabel(properties.data?.data ?? [], value) : ''
                }
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {allowedIds.map((id) => (
                <SelectItem key={id} value={id}>
                  {propertyLabel(properties.data?.data ?? [], id)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      )}
      {properties.isPending && canWrite && <p role="status">지점 목록을 불러오고 있습니다.</p>}
      {properties.isError && <p role="alert">지점 목록을 불러오지 못했습니다.</p>}
      {properties.data?.next_cursor && !propertyId && (
        <Button
          variant="outline"
          disabled={properties.isFetching || pending || Boolean(attempt)}
          onClick={() => {
            setPropertyCursor(properties.data.next_cursor);
          }}
        >
          다음 지점 50개
        </Button>
      )}
      {propertyId && catalog.isPending && (
        <p role="status">선택한 지점 범위를 확인하고 있습니다.</p>
      )}
      {propertyId && catalog.isError && <p role="alert">선택한 지점 범위를 확인하지 못했습니다.</p>}
      {propertyId && context.isError && (
        <Alert variant="warning">
          <AlertDescription>
            선택한 지점의 퇴역 범위를 확인하지 못했습니다. 다시 확인해 주세요.
          </AlertDescription>
        </Alert>
      )}
      {propertyId && !context.isPending && context.data && !contextMatchesRow && (
        <Alert variant="warning">
          <AlertDescription>
            최신 장비 정보와 퇴역 범위의 revision이 일치하지 않습니다. 새 초안으로 진행할 수
            없습니다.
          </AlertDescription>
        </Alert>
      )}
      {error && (
        <Alert variant="warning">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {reconcileRequired && (
        <p className="caption text-muted-foreground">
          최신 장비 정보와 퇴역 범위를 확인할 때까지 새 요청을 보낼 수 없습니다.
        </p>
      )}
      {uncertain && (
        <Button disabled={pending || !canWrite} onClick={() => void submit(true)}>
          같은 퇴역 요청 다시 보내기
        </Button>
      )}
      {receipt && (
        <div className="space-y-2">
          <p role="status">
            퇴역 저장을 확인했습니다. 실제 장비를 끄거나 출입 권한을 회수하지는 않습니다.
          </p>
          <p className="body-strong">퇴역 처리한 장비 · {device.device_id}</p>
          {readError && (
            <p role="alert">퇴역 저장은 확인했지만 장비 목록을 다시 불러오지 못했습니다.</p>
          )}
          <Button variant="outline" onClick={close}>
            영수증 닫기
          </Button>
        </div>
      )}
      {!receipt && !attempt && !uncertain && (
        <Button
          tone="brand"
          disabled={!canStart}
          onClick={() => {
            setConfirmOpen(true);
          }}
        >
          퇴역 확인 보기
        </Button>
      )}
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        target={`장비 ID ${device.device_id}`}
        action="장비 퇴역"
        consequence={`현재 저장된 연결 ${String(device.slot_ids.length)}개를 퇴역 기록에서 해제합니다. Core 등록 정보만 퇴역하며 실제 장비 작동이나 출입 권한은 바뀌지 않습니다.`}
        variant="destructive"
        onConfirm={() => {
          setConfirmOpen(false);
          void submit(false);
        }}
      />
      <ConfirmDialog
        open={closeConfirmOpen}
        onOpenChange={setCloseConfirmOpen}
        target={uncertain ? '결과가 불확실한 퇴역 요청' : '퇴역 초안'}
        action="초안 폐기 후 닫기"
        consequence={
          uncertain
            ? '앞선 요청은 이미 저장되었을 수 있습니다. 화면을 떠나면 같은 요청을 다시 확인할 내용은 사라집니다.'
            : '아직 보내지 않은 퇴역 초안이 사라집니다.'
        }
        onConfirm={close}
      />
    </section>
  );
}
